import { useState } from "react"

import {
  getAuth,
  OAuthProvider,
  signInWithPopup,
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  fetchSignInMethodsForEmail,
  browserPopupRedirectResolver,
} from "firebase/auth"
import { useForm } from "react-hook-form"
import { initializeApp } from "firebase/app"
import { getDatabase } from "firebase/database"

import { Helpers } from "@flexday/common-react"

import { createUser } from "./create-user"
import Input from "../reusable-components/input"
import checkIcon from "../assets/icons/check.svg"
import Button from "../reusable-components/button"
import SignUpWithAppleImage from "../assets/images/apple_su.png"
import SignUpWithGoogleImage from "../assets/images/google_su.svg"

const Signup = ({ onError, onSuccess, createAccountUrl }) => {
  const { getFirebaseConfig } = Helpers
  const [isLinkSent, setIsLinkSet] = useState(false)
  const [response, setResponse] = useState({ message: "", color: "" })
  const queryParameters = new URLSearchParams(window.location.search)
  const redirectUrl = queryParameters.get("redirectUrl")

  const {
    watch,
    reset,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm()

  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })
  const app = initializeApp(firebaseConfig)
  const database = getDatabase(app)

  const onCreateAccount = async ({ firstName, lastName, email }) => {
    setIsLinkSet(false)
    setResponse({ message: "", color: "" })

    const settings = {
      url: redirectUrl
        ? `${createAccountUrl}?redirectUrl=${redirectUrl}`
        : createAccountUrl,
      handleCodeInApp: true,
    }

    try {
      const auth = getAuth(app)
      localStorage.setItem(
        "userInfo",
        JSON.stringify({ email, firstName, lastName })
      )
      const emailExists = !!(await fetchSignInMethodsForEmail(auth, email))
        ?.length

      if (emailExists) {
        setResponse({
          color: "text-[#ff0000]",
          message: "Email already exists.",
        })

        return
      }

      await sendSignInLinkToEmail(auth, email, settings)

      setResponse({
        color: "text-[#10b981]",
        message: "An email has been sent!",
      })

      reset({ firstName, lastName, email })
      setIsLinkSet(true)
      onSuccess()
    } catch (error) {
      onError(error)
    }
  }

  const onSignUpWithGoogle = async () => {
    setResponse({ message: "", color: "" })

    try {
      const auth = getAuth(app)
      const provider = new GoogleAuthProvider()

      const { user } = await signInWithPopup(
        auth,
        provider,
        browserPopupRedirectResolver
      )

      await createUser(database, user)
      onSuccess()
    } catch (error) {
      onError(error)
    }
  }

  const onSignUpWithApple = async () => {
    setResponse({ message: "", color: "" })

    try {
      const auth = getAuth(app)
      const provider = new OAuthProvider("apple.com")

      const { user } = await signInWithPopup(auth, provider)

      await createUser(database, user)
      onSuccess()
    } catch (error) {
      onError(error)
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={handleSubmit(onCreateAccount)}>
      <p className="mb-3 text-[26px] font-extrabold">Create your account</p>
      <Input
        name="firstName"
        register={register}
        placeholder="First Name"
        className="w-full max-w-sm"
      />
      <Input
        name="lastName"
        register={register}
        placeholder="Last Name"
        className="w-full max-w-sm"
      />
      <div className="relative max-w-sm">
        <Input
          required
          name="email"
          type="email"
          className="w-full"
          placeholder="Email"
          register={register}
        />
        {isLinkSent && (
          <img
            alt="success"
            src={checkIcon}
            className="absolute right-[-34px] top-2.5"
          />
        )}
      </div>
      <div className="relative flex flex-col justify-center gap-2 w-48 pb-5">
        <Button
          type="submit"
          title="Create Account"
          disabled={!watch("email") || !isDirty}
          className="h-14 font-bold bg-purple-400 hover:bg-purple-500
           disabled:bg-purple-300 disabled:hover:bg-purple-300
           disabled:cursor-not-allowed transition-colors
           duration-300 ease-in-out"
        />
        <p className="text-xs text-center italic text-black font-light">
          Sends an email confirmation link.
        </p>
        <p
          className={`absolute bottom-0 text-sm font-medium ${response.color}`}>
          {response.message}
        </p>
      </div>
      <div className="flex flex-col gap-8 mt-10">
        <p>or create an account with:</p>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button type="button">
            <img
              src={SignUpWithAppleImage}
              onClick={onSignUpWithApple}
              className="h-10"
            />
          </button>
          <button type="button">
            <img
              src={SignUpWithGoogleImage}
              onClick={onSignUpWithGoogle}
              className="h-10"
            />
          </button>
        </div>
      </div>
    </form>
  )
}

export default Signup
